import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Plus, Trash2, Edit2, Check, X, Calendar, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, CalendarIcon, Download, Clock } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { Label } from "@/components/ui/label";
import { useGoogleCalendarSync } from "@/hooks/useGoogleCalendarSync";
import { calendarDatesFromPosition, calendarDatesFromSelection, calendarDatesFromDrag } from "@/lib/calendarDates";

// Group calendar email notifications are now sent automatically by a database
// trigger on lead_gantt_items, covering all CRUD avenues (UI, AI, GCal sync).
function notifyGroupCalendarChange(
  _action: 'created' | 'updated' | 'deleted',
  _groupId: string | null,
  _leadId: string,
  _leadName: string | undefined,
  _item: { id?: string; title?: string | null; start_date?: string | null; end_date?: string | null; start_time?: string | null; is_all_day?: boolean | null; notes?: string | null }
) {
  // no-op: handled by Postgres trigger -> notify-gantt-change
}

interface GanttItem {
  id: string;
  lead_id: string;
  user_id: string;
  group_id: string | null;
  title: string;
  notes: string | null;
  start_position: number;
  width: number;
  row_index: number;
  color: string;
  created_at: string;
  updated_at: string;
  google_event_id?: string | null;
  last_synced_at?: string | null;
  start_time?: string | null;
  is_all_day?: boolean;
  time_duration?: number;
  start_date?: string | null;
  end_date?: string | null;
}

// Compute absolute YYYY-MM-DD dates from position/width relative to today
// end_date is EXCLUSIVE — a 1-day task on Jan 5 has start_date=2025-01-05, end_date=2025-01-06
function computeAbsoluteDates(startPosition: number, width: number): { start_date: string; end_date: string } {
  return calendarDatesFromPosition(startPosition, width);
}

interface LeadGanttChartProps {
  leadId: string;
  leadName?: string;
  userId: string;
  groupId: string | null;
  groupName?: string;
  aggregatedLeadIds?: string[];
}

const COLORS = [
  "#3b82f6", // blue
  "#22c55e", // green
  "#eab308", // yellow
  "#f97316", // orange
  "#ef4444", // red
  "#8b5cf6", // purple
  "#06b6d4", // cyan
  "#ec4899", // pink
];

const ZOOM_LEVELS = [4, 8, 12, 20, 30, 40, 60, 80, 100, 120];
const ROW_HEIGHT = 60;
const EXPORT_ROW_HEIGHT = 110;
const MIN_TIMELINE_DAYS = 14;
const PADDING_DAYS = 3;
const BASE_DAY_WIDTH = 80; // Storage uses this as reference

// Drag state stored outside component to avoid stale closures
interface DragState {
  item: GanttItem | null;
  type: "move" | "resize-left" | "resize-right" | null;
  startX: number;
  startPosition: number;
  startWidth: number;
  currentPosition: number;
  currentWidth: number;
  isDragging: boolean;
}

const LeadGanttChart = ({ leadId, leadName, userId, groupId, groupName: propGroupName, aggregatedLeadIds }: LeadGanttChartProps) => {
  const isAggregatedMode = !!aggregatedLeadIds && aggregatedLeadIds.length > 0;
  const [items, setItems] = useState<GanttItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [isExportMode, setIsExportMode] = useState(false);
  const [editingItem, setEditingItem] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");
  const [editNotes, setEditNotes] = useState("");
  const [editStartDate, setEditStartDate] = useState("");
  const [editDuration, setEditDuration] = useState<number | string>(1);
  const [editStartTime, setEditStartTime] = useState("09:00");
  const [editIsAllDay, setEditIsAllDay] = useState(true);
  const [editTimeDuration, setEditTimeDuration] = useState(60);
  const [zoomLevel, setZoomLevel] = useState(7);
  const [, forceUpdate] = useState({});
  const [overflowingItems, setOverflowingItems] = useState<Set<string>>(new Set());
  const titleRefs = useRef<Map<string, HTMLSpanElement>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);
  const [fetchedGroupName, setFetchedGroupName] = useState<string | null>(null);

  // Check which item titles overflow their bars
  useEffect(() => {
    const newOverflow = new Set<string>();
    titleRefs.current.forEach((el, id) => {
      if (el.scrollWidth > el.clientWidth) {
        newOverflow.add(id);
      }
    });
    setOverflowingItems(prev => {
      if (prev.size !== newOverflow.size || [...newOverflow].some(id => !prev.has(id))) return newOverflow;
      return prev;
    });
  }, [items, zoomLevel]);

  useEffect(() => {
    if (groupId && !propGroupName) {
      supabase
        .from('groups')
        .select('name')
        .eq('id', groupId)
        .single()
        .then(({ data }) => {
          if (data?.name) {
            setFetchedGroupName(data.name);
          }
        });
    }
  }, [groupId, propGroupName]);

  const effectiveGroupName = propGroupName || fetchedGroupName || 'Group';
  
  // Workspace info for calendar sync
  const workspaceInfo = useMemo(() => ({
    workspaceType: groupId ? 'group' as const : 'personal' as const,
    groupId: groupId,
    workspaceName: groupId ? effectiveGroupName : 'Personal',
  }), [groupId, effectiveGroupName]);
  
  const { 
    isConnected, 
    syncItem, 
    syncGroupItem,
    deleteItem: deleteCalendarItem,
    pullChanges,
  } = useGoogleCalendarSync(workspaceInfo);

  // Keep refs to volatile values so realtime callbacks always see the latest value
  // without needing to re-subscribe the channel (which would cause missed events).
  const isConnectedRef = useRef(false);
  const syncItemRef = useRef(syncItem);
  const deleteCalendarItemRef = useRef(deleteCalendarItem);
  const leadNameRef = useRef(leadName);
  useEffect(() => { isConnectedRef.current = isConnected; }, [isConnected]);
  useEffect(() => { syncItemRef.current = syncItem; }, [syncItem]);
  useEffect(() => { deleteCalendarItemRef.current = deleteCalendarItem; }, [deleteCalendarItem]);
  useEffect(() => { leadNameRef.current = leadName; }, [leadName]);

  const pullInFlightRef = useRef(false);
  const hasStartedPollingRef = useRef(false);

  // Background pull (two-way sync): keep Gantt updated when Google events change.
  // Always pull on open when connected — even if no local items have google_event_id yet,
  // because events may have been created or deleted directly in Google Calendar.
  useEffect(() => {
    if (!isConnected || isLoading) return;

    let cancelled = false;

    const runPull = async () => {
      if (pullInFlightRef.current) return;
      pullInFlightRef.current = true;
      try {
        const updated = await pullChanges(leadId);
        if (!cancelled && updated > 0) {
          await fetchItems();
        }
      } catch (err) {
        console.error('Auto-pull error:', err);
      } finally {
        pullInFlightRef.current = false;
      }
    };

    // Always run once when the Gantt chart opens for this lead
    if (!hasStartedPollingRef.current) {
      hasStartedPollingRef.current = true;
      runPull();
    }

    const interval = window.setInterval(runPull, 30000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [isConnected, isLoading, leadId, pullChanges]);

  useEffect(() => {
    hasStartedPollingRef.current = false;
  }, [leadId]);

  // Use ref for drag state to avoid stale closure issues
  const dragStateRef = useRef<DragState>({
    item: null,
    type: null,
    startX: 0,
    startPosition: 0,
    startWidth: 0,
    currentPosition: 0,
    currentWidth: 0,
    isDragging: false,
  });

  const DAY_WIDTH = ZOOM_LEVELS[zoomLevel];

  // Calculate dynamic timeline based on items
  // Build guaranteed local-midnight Date (immune to DST transitions unlike setHours)
  const localMidnight = useCallback((offsetDays = 0): Date => {
    const n = new Date();
    const d = new Date(n.getFullYear(), n.getMonth(), n.getDate() + offsetDays);
    return d;
  }, []);

  const timelineBounds = useMemo(() => {
    if (items.length === 0) {
      return { startDate: localMidnight(-PADDING_DAYS), days: MIN_TIMELINE_DAYS + PADDING_DAYS * 2 };
    }

    const todayMid = localMidnight();
    const msPerDay = 24 * 60 * 60 * 1000;

    // Track earliest/latest as integer day offsets from today
    let earliestOffset = 0; // today
    let latestEndOffset = MIN_TIMELINE_DAYS;

    items.forEach(item => {
      let itemStartOffset: number;
      let itemDurationDays: number;

      // Use absolute dates as source of truth when available (matches rendering logic)
      if (item.start_date) {
        const [y, m, d] = item.start_date.split('-').map(Number);
        const startMs = new Date(y, m - 1, d).getTime();
        itemStartOffset = Math.round((startMs - todayMid.getTime()) / msPerDay);
      } else {
        itemStartOffset = Math.floor(item.start_position / BASE_DAY_WIDTH);
      }

      if (item.start_date && item.end_date) {
        const [sy, sm, sd] = item.start_date.split('-').map(Number);
        const [ey, em, ed] = item.end_date.split('-').map(Number);
        const startMs = new Date(sy, sm - 1, sd).getTime();
        const endMs = new Date(ey, em - 1, ed).getTime();
        // end_date is exclusive, so duration = (end - start) / msPerDay
        itemDurationDays = Math.max(1, Math.round((endMs - startMs) / msPerDay));
      } else {
        itemDurationDays = Math.max(1, Math.round(item.width / BASE_DAY_WIDTH));
      }

      const itemEndOffset = itemStartOffset + itemDurationDays;

      if (itemStartOffset < earliestOffset) earliestOffset = itemStartOffset;
      if (itemEndOffset > latestEndOffset) latestEndOffset = itemEndOffset;
    });

    // Apply padding using integer offsets — localMidnight guarantees clean midnight boundaries
    const paddedStart = earliestOffset - PADDING_DAYS;
    const paddedEnd = latestEndOffset + PADDING_DAYS;
    const totalDays = Math.max(MIN_TIMELINE_DAYS, paddedEnd - paddedStart);

    return { startDate: localMidnight(paddedStart), days: totalDays };
  }, [items, localMidnight]);

  const { startDate: timelineStartDate, days: timelineDays } = timelineBounds;

  // Helper functions for position/date conversion
  const getItemStartDate = useCallback((item: GanttItem) => {
    // Use absolute start_date as source of truth when available
    if (item.start_date) {
      const [y, m, d] = item.start_date.split('-').map(Number);
      return new Date(y, m - 1, d); // local midnight
    }
    // Fallback for legacy items without start_date
    const startDays = Math.floor(item.start_position / BASE_DAY_WIDTH);
    return localMidnight(startDays);
  }, [localMidnight]);

  const getItemDuration = useCallback((item: GanttItem) => {
    // Use absolute dates as source of truth when available
    if (item.start_date && item.end_date) {
      const [sy, sm, sd] = item.start_date.split('-').map(Number);
      const [ey, em, ed] = item.end_date.split('-').map(Number);
      const startMs = new Date(sy, sm - 1, sd).getTime();
      const endMs = new Date(ey, em - 1, ed).getTime();
      // end_date is exclusive, so duration = (end - start) / msPerDay
      const days = Math.round((endMs - startMs) / (24 * 60 * 60 * 1000));
      return Math.max(1, days);
    }
    return Math.max(1, Math.round(item.width / BASE_DAY_WIDTH));
  }, []);

  const dateToVisualPosition = useCallback((date: Date) => {
    // Use integer day parts to avoid floating-point and DST rounding errors
    const toIntDay = (d: Date) => d.getFullYear() * 10000 + d.getMonth() * 100 + d.getDate();
    // Fast path: if both are local-midnight Dates, ms diff is exact
    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    const diffMs = date.getTime() - timelineStartDate.getTime();
    const diffDays = Math.round(diffMs / MS_PER_DAY);
    return diffDays * DAY_WIDTH;
  }, [timelineStartDate, DAY_WIDTH]);

  const getItemVisualPosition = useCallback((item: GanttItem) => {
    const itemDate = getItemStartDate(item);
    return dateToVisualPosition(itemDate);
  }, [getItemStartDate, dateToVisualPosition]);

  const getItemVisualWidth = useCallback((item: GanttItem) => {
    const days = getItemDuration(item);
    return days * DAY_WIDTH;
  }, [getItemDuration, DAY_WIDTH]);

  // Convert pixel delta to storage units
  const pixelToStorageDelta = useCallback((pixelDelta: number) => {
    const daysDelta = pixelDelta / DAY_WIDTH;
    return daysDelta * BASE_DAY_WIDTH;
  }, [DAY_WIDTH]);

  // Zoom handlers
  const handleZoomIn = () => {
    if (zoomLevel < ZOOM_LEVELS.length - 1) {
      setZoomLevel(prev => prev + 1);
    }
  };

  const handleZoomOut = () => {
    if (zoomLevel > 0) {
      setZoomLevel(prev => prev - 1);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [leadId]);

  // Real-time subscription for group workspaces
  useEffect(() => {
    if (!groupId) return; // Only subscribe for group workspaces

    // Use a stable channel name so it never tears down/recreates due to dep changes.
    // All volatile state (syncItem, deleteCalendarItem, leadName) is accessed via refs.
    const channelName = `gantt-group-${groupId}-lead-${leadId}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'lead_gantt_items',
          filter: `lead_id=eq.${leadId}`,
        },
        (payload) => {
          // For INSERT/UPDATE, check user_id on the new record.
          // For DELETE, payload.old only contains the PK (Supabase default) so we
          // cannot reliably check user_id — treat ALL deletes as potentially from another user
          // and guard with the items ref to avoid double-deleting own actions.
          const isDelete = payload.eventType === 'DELETE';
          const recordUserId = isDelete
            ? (payload.old as any)?.user_id  // may be undefined if not in replica identity
            : (payload.new as any)?.user_id;

          // fromOtherUser: true when we definitively know it's another user, OR when we
          // can't tell (DELETE without user_id in payload) — we still process it so UI stays in sync.
          const fromOtherUser = recordUserId === undefined || recordUserId !== userId;

          if (payload.eventType === 'INSERT') {
            const newItem = payload.new as GanttItem;
            setItems(prev => {
              if (prev.some(item => item.id === newItem.id)) return prev;
              return [...prev, newItem];
            });
            // For group items, sync-group already pushed to all members' calendars,
            // so we do NOT re-sync here (which would overwrite the shared google_event_id
            // and create duplicate events). Only sync for personal items.
            if (fromOtherUser && isConnectedRef.current && !groupId) {
              syncItemRef.current(newItem, leadNameRef.current || 'Lead', leadId);
            }
          } else if (payload.eventType === 'UPDATE') {
            const updatedItem = payload.new as GanttItem;
            setItems(prev => prev.map(item =>
              item.id === updatedItem.id ? updatedItem : item
            ));
            // Same as INSERT: skip re-sync for group items to avoid duplicates
            if (fromOtherUser && isConnectedRef.current && !groupId) {
              syncItemRef.current(updatedItem, leadNameRef.current || 'Lead', leadId);
            }
          } else if (payload.eventType === 'DELETE') {
            const deletedItem = payload.old as { id: string; google_event_id?: string; user_id?: string };
            setItems(prev => {
              const exists = prev.some(item => item.id === deletedItem.id);
              if (!exists) return prev; // already removed (own action), skip
              return prev.filter(item => item.id !== deletedItem.id);
            });
            // Remove from Google Calendar — use stored items to find google_event_id
            // since payload.old may not include non-PK columns
            if (isConnectedRef.current) {
              const googleEventId = deletedItem.google_event_id;
              deleteCalendarItemRef.current(googleEventId, deletedItem.id);
            }
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log(`[GanttRealtime] Subscribed to ${channelName}`);
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn(`[GanttRealtime] Channel issue: ${status} on ${channelName}`);
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  // Only re-subscribe when the lead or group changes — NOT when callbacks change.
  // Callbacks are accessed via refs so the channel stays stable.
  }, [leadId, groupId, userId]);

  const fetchItems = async () => {
    setIsLoading(true);
    try {
      let query = supabase
        .from("lead_gantt_items")
        .select("*")
        .order("row_index", { ascending: true });

      if (isAggregatedMode) {
        query = query.in("lead_id", aggregatedLeadIds!);
      } else {
        query = query.eq("lead_id", leadId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setItems((data as GanttItem[]) || []);
    } catch (error) {
      console.error("Error fetching gantt items:", error);
      toast.error("Failed to load progress tracker");
    } finally {
      setIsLoading(false);
    }
  };

  const notifyCalendarChanged = () => {
    window.dispatchEvent(new CustomEvent('gantt-items-changed'));
  };

  const addItem = async () => {
    const maxRow = items.length > 0 ? Math.max(...items.map(i => i.row_index)) + 1 : 0;
    
    try {
      const { data, error } = await supabase
        .from("lead_gantt_items")
        .insert({
          lead_id: leadId,
          user_id: userId,
          group_id: groupId,
          title: "New Task",
          notes: "",
          start_position: 0,
          width: BASE_DAY_WIDTH * 1,
          row_index: maxRow,
          color: COLORS[items.length % COLORS.length],
          ...computeAbsoluteDates(0, BASE_DAY_WIDTH * 1),
        })
        .select()
        .single();

      if (error) throw error;
      const newItem = data as GanttItem;
      setItems((prev) => [...prev, newItem]);
      notifyCalendarChanged();
      toast.success("Task added");
      notifyGroupCalendarChange('created', groupId, leadId, leadName, newItem);

      if (isConnected) {
        if (groupId) {
          // Push to ALL group members' calendars (including inactive users)
          syncGroupItem(newItem, leadName || 'Lead', leadId);
        } else {
          syncItem(newItem, leadName || 'Lead', leadId);
        }
      }
    } catch (error) {
      console.error("Error adding item:", error);
      toast.error("Failed to add task");
    }
  };

  const updateItem = async (id: string, updates: Partial<GanttItem>) => {
    const updatedItem = items.find(item => item.id === id);
    const newItem = updatedItem ? { ...updatedItem, ...updates } : null;
    
    setItems(prev => prev.map(item => item.id === id ? { ...item, ...updates } : item));
    
    try {
      const { error } = await supabase
        .from("lead_gantt_items")
        .update(updates)
        .eq("id", id);

      if (error) throw error;
      notifyCalendarChanged();
      if (newItem) notifyGroupCalendarChange('updated', groupId, leadId, leadName, newItem as GanttItem);

      if (newItem && isConnected) {
        if (groupId) {
          // Push to ALL group members' calendars
          syncGroupItem(newItem as GanttItem, leadName || 'Lead', leadId);
        } else {
          syncItem(newItem as GanttItem, leadName || 'Lead', leadId);
        }
      }
    } catch (error) {
      console.error("Error updating item:", error);
      toast.error("Failed to update task");
      fetchItems();
    }
  };

  const deleteItem = async (id: string) => {
    const itemToDelete = items.find(item => item.id === id);
    const googleEventId = (itemToDelete as any)?.google_event_id;
    
    try {
      const { error } = await supabase
        .from("lead_gantt_items")
        .delete()
        .eq("id", id);

      if (error) throw error;
      setItems((prev) => prev.filter(item => item.id !== id));
      notifyCalendarChanged();
      toast.success("Task deleted");
      if (itemToDelete) notifyGroupCalendarChange('deleted', groupId, leadId, leadName, itemToDelete);

      if (isConnected) {
        if (groupId && itemToDelete) {
          // Delete from ALL group members' calendars
          syncGroupItem(itemToDelete, leadName || 'Lead', leadId, 'delete');
        } else {
          deleteCalendarItem(googleEventId, id);
        }
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete task");
    }
  };

  // Export to PDF (export-mode renders title-only and no truncation)
  const exportToPdf = async () => {
    if (!containerRef.current || items.length === 0) {
      toast.error("No tasks to export");
      return;
    }

    const container = containerRef.current;
    const nextFrame = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

    // Store original styles so we can restore even if capture fails
    const originalStyle = {
      maxHeight: container.style.maxHeight,
      overflow: container.style.overflow,
    };

    setIsExporting(true);
    toast.info("Generating PDF...");

    try {
      // Switch the chart into an export-friendly render:
      // - title only
      // - no truncation
      // - taller rows/bars
      setIsExportMode(true);
      await nextFrame();
      await nextFrame();

      // Expand to show all content including the timeline header
      container.style.maxHeight = "none";
      container.style.overflow = "visible";

      // Let layout settle
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Capture the full container including timeline header
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#1a1a2e",
        width: container.scrollWidth,
        height: container.scrollHeight,
        scrollX: 0,
        scrollY: 0,
      });

      // Create PDF with appropriate dimensions
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      
      // Use landscape orientation for wide charts
      const orientation = imgWidth > imgHeight ? "landscape" : "portrait";
      const pdf = new jsPDF({
        orientation,
        unit: "px",
        format: [imgWidth / 2, imgHeight / 2], // Divide by scale factor
      });

      // Add the image to the PDF
      const imgData = canvas.toDataURL("image/png");
      pdf.addImage(imgData, "PNG", 0, 0, imgWidth / 2, imgHeight / 2);

      // Download the PDF
      const fileName = leadName
        ? `gantt-${leadName.toLowerCase().replace(/\s+/g, "-")}-${format(new Date(), "yyyy-MM-dd")}.pdf`
        : `gantt-${format(new Date(), "yyyy-MM-dd")}.pdf`;
      pdf.save(fileName);

      toast.success("PDF exported!");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Failed to export PDF");
    } finally {
      // Restore styles and render mode
      container.style.maxHeight = originalStyle.maxHeight;
      container.style.overflow = originalStyle.overflow;
      setIsExportMode(false);
      setIsExporting(false);
    }
  };

  // Pointer event handlers for smooth dragging
  const handlePointerDown = useCallback((e: React.PointerEvent, item: GanttItem, type: "move" | "resize-left" | "resize-right") => {
    e.preventDefault();
    e.stopPropagation();
    
    // Capture pointer for smooth tracking even outside element
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    
    dragStateRef.current = {
      item,
      type,
      startX: e.clientX,
      startPosition: item.start_position,
      startWidth: item.width,
      currentPosition: item.start_position,
      currentWidth: item.width,
      isDragging: true,
    };
    
    forceUpdate({});
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    const state = dragStateRef.current;
    if (!state.isDragging || !state.item) return;

    const deltaX = e.clientX - state.startX;
    const storageDelta = pixelToStorageDelta(deltaX);
    
    // Snap to day boundaries in storage units
    const snappedDelta = Math.round(storageDelta / BASE_DAY_WIDTH) * BASE_DAY_WIDTH;

    if (state.type === "move") {
      state.currentPosition = state.startPosition + snappedDelta;
    } else if (state.type === "resize-right") {
      const newWidth = Math.max(BASE_DAY_WIDTH, state.startWidth + snappedDelta);
      state.currentWidth = newWidth;
    } else if (state.type === "resize-left") {
      const newWidth = Math.max(BASE_DAY_WIDTH, state.startWidth - snappedDelta);
      if (newWidth >= BASE_DAY_WIDTH) {
        state.currentWidth = newWidth;
        state.currentPosition = state.startPosition + snappedDelta;
      }
    }

    forceUpdate({});
  }, [pixelToStorageDelta]);

  const handlePointerUp = useCallback(async (e: React.PointerEvent) => {
    const state = dragStateRef.current;
    if (!state.isDragging || !state.item) return;

    (e.target as HTMLElement).releasePointerCapture(e.pointerId);

    // Apply the final position/width to state
    const updates: Partial<GanttItem> = {};
    
    if (state.currentPosition !== state.startPosition) {
      updates.start_position = state.currentPosition;
    }
    if (state.currentWidth !== state.startWidth) {
      updates.width = state.currentWidth;
    }

    // Recompute absolute dates whenever position or width changed
    if (Object.keys(updates).length > 0) {
      const finalPosition = updates.start_position ?? state.item.start_position;
      const finalWidth = updates.width ?? state.item.width;
      Object.assign(updates, calendarDatesFromDrag(state.item, finalPosition, finalWidth));
      await updateItem(state.item.id, updates);
    }

    dragStateRef.current = {
      item: null,
      type: null,
      startX: 0,
      startPosition: 0,
      startWidth: 0,
      currentPosition: 0,
      currentWidth: 0,
      isDragging: false,
    };

    forceUpdate({});
  }, [updateItem]);

  // Get visual values accounting for drag state
  const getDisplayPosition = useCallback((item: GanttItem) => {
    const state = dragStateRef.current;
    if (state.isDragging && state.item?.id === item.id) {
      const dates = calendarDatesFromDrag(item, state.currentPosition, state.currentWidth);
      const [y, m, d] = dates.start_date.split('-').map(Number);
      const itemDate = new Date(y, m - 1, d);
      return dateToVisualPosition(itemDate);
    }
    return getItemVisualPosition(item);
  }, [dateToVisualPosition, getItemVisualPosition, localMidnight]);

  const getDisplayWidth = useCallback((item: GanttItem) => {
    const state = dragStateRef.current;
    if (state.isDragging && state.item?.id === item.id) {
      const days = Math.max(1, Math.round(state.currentWidth / BASE_DAY_WIDTH));
      return days * DAY_WIDTH;
    }
    return getItemVisualWidth(item);
  }, [DAY_WIDTH, getItemVisualWidth]);

  const startEditing = (item: GanttItem) => {
    setEditingItem(item.id);
    setEditTitle(item.title);
    setEditNotes(item.notes || "");
    setEditIsAllDay(item.is_all_day !== false); // default true
    setEditStartTime(item.start_time || "09:00");
    setEditTimeDuration(item.time_duration ?? 60);
    
    const itemStartDate = getItemStartDate(item);
    // Use local date parts to avoid UTC-shift (toISOString converts to UTC, shifting back 1 day in negative-offset timezones)
    const y = itemStartDate.getFullYear();
    const m = String(itemStartDate.getMonth() + 1).padStart(2, '0');
    const d = String(itemStartDate.getDate()).padStart(2, '0');
    setEditStartDate(`${y}-${m}-${d}`);
    setEditDuration(getItemDuration(item));
  };

  const saveEdit = async (id: string) => {
    const dates = calendarDatesFromSelection(editStartDate, Number(editDuration) || 1);
    await updateItem(id, { 
      title: editTitle, 
      notes: editNotes,
      start_position: dates.start_position,
      width: dates.width,
      start_time: editIsAllDay ? null : editStartTime,
      is_all_day: editIsAllDay,
      time_duration: editIsAllDay ? 60 : editTimeDuration,
      start_date: dates.start_date,
      end_date: dates.end_date,
    });
    setEditingItem(null);
  };

  const formatTimestamp = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Timeline mode based on zoom level
  const getTimelineMode = () => {
    if (DAY_WIDTH <= 8) return 'months';
    if (DAY_WIDTH <= 20) return 'weeks';
    return 'days';
  };

  const timelineMode = getTimelineMode();

  const getTimelineBlocks = () => {
    const blocks: { date: Date; label: string; width: number; isToday: boolean }[] = [];
    
    if (timelineMode === 'months') {
      let currentDate = new Date(timelineStartDate);
      const endDate = new Date(timelineStartDate);
      endDate.setDate(endDate.getDate() + timelineDays);
      
      while (currentDate < endDate) {
        const monthStart = new Date(currentDate);
        const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        const daysInBlock = Math.min(
          Math.ceil((monthEnd.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24)) + 1,
          Math.ceil((endDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24))
        );
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const isToday = today.getMonth() === currentDate.getMonth() && today.getFullYear() === currentDate.getFullYear();
        
        blocks.push({
          date: monthStart,
          label: currentDate.toLocaleDateString("en-US", { month: "short", year: "numeric" }),
          width: daysInBlock * DAY_WIDTH,
          isToday
        });
        
        currentDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1);
      }
    } else if (timelineMode === 'weeks') {
      let currentDate = new Date(timelineStartDate);
      const endDate = new Date(timelineStartDate);
      endDate.setDate(endDate.getDate() + timelineDays);
      
      while (currentDate < endDate) {
        const weekStart = new Date(currentDate);
        const daysUntilSunday = 7 - currentDate.getDay();
        const weekEnd = new Date(currentDate);
        weekEnd.setDate(weekEnd.getDate() + daysUntilSunday);
        
        const daysInBlock = Math.min(
          daysUntilSunday + 1,
          Math.ceil((endDate.getTime() - currentDate.getTime()) / (1000 * 60 * 60 * 24))
        );
        
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const isToday = today >= currentDate && today <= weekEnd;
        
        const weekEndDisplay = new Date(currentDate);
        weekEndDisplay.setDate(weekEndDisplay.getDate() + Math.min(6, daysInBlock - 1));
        
        blocks.push({
          date: weekStart,
          label: `${currentDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${weekEndDisplay.toLocaleDateString("en-US", { day: "numeric" })}`,
          width: daysInBlock * DAY_WIDTH,
          isToday
        });
        
        currentDate = new Date(currentDate);
        currentDate.setDate(currentDate.getDate() + daysInBlock);
      }
    } else {
      // Compute the day offset of timelineStartDate relative to today
      const todayMidnight = localMidnight();
      const MS_PER_DAY = 24 * 60 * 60 * 1000;
      const timelineStartOffset = Math.round((timelineStartDate.getTime() - todayMidnight.getTime()) / MS_PER_DAY);

      for (let i = 0; i < timelineDays; i++) {
        // Use localMidnight with integer offset — guaranteed clean midnight, no DST drift
        const date = localMidnight(timelineStartOffset + i);
        const todayStr = `${todayMidnight.getFullYear()}-${todayMidnight.getMonth()}-${todayMidnight.getDate()}`;
        const dateStr = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

        blocks.push({
          date,
          label: date.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
          width: DAY_WIDTH,
          isToday: dateStr === todayStr,
        });
      }
    }
    
    return blocks;
  };

  const getItemDateRange = (item: GanttItem) => {
    const itemStart = getItemStartDate(item);
    const durationDays = getItemDuration(item);
    
    const itemEnd = new Date(itemStart);
    itemEnd.setDate(itemEnd.getDate() + durationDays - 1);
    
    return {
      start: itemStart.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      end: itemEnd.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
      duration: durationDays
    };
  };

  const timelineWidth = timelineDays * DAY_WIDTH;
  const rowHeight = isExportMode ? EXPORT_ROW_HEIGHT : ROW_HEIGHT;
  const barHeight = isExportMode ? rowHeight - 16 : 44;
  const isDragging = dragStateRef.current.isDragging;
  const dragItemId = dragStateRef.current.item?.id;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32 text-muted-foreground">
        Loading progress tracker...
      </div>
    );
  }

  return (
    <div className="space-y-3 w-full">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium">Progress Tracker</Label>
          <span className="text-xs text-muted-foreground">
            (Starting: {timelineStartDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })})
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1 border rounded-md px-1">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleZoomOut} 
              disabled={zoomLevel === 0}
              className="h-6 w-6"
              title="Zoom out"
            >
              <ZoomOut className="h-3 w-3" />
            </Button>
            <span className="text-xs text-muted-foreground w-10 text-center">
              {Math.round((DAY_WIDTH / 80) * 100)}%
            </span>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleZoomIn} 
              disabled={zoomLevel === ZOOM_LEVELS.length - 1}
              className="h-6 w-6"
              title="Zoom in"
            >
              <ZoomIn className="h-3 w-3" />
            </Button>
          </div>
          <Button variant="outline" size="sm" onClick={addItem} className="h-7 text-xs">
            <Plus className="h-3 w-3 mr-1" />
            Add Task
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={exportToPdf} 
            disabled={isExporting || items.length === 0}
            className="h-7 text-xs"
            title="Export as PDF"
          >
            <Download className="h-3 w-3 mr-1" />
            {isExporting ? "Exporting..." : "PDF"}
          </Button>
          
        </div>
      </div>

      <div 
        ref={containerRef}
        className="relative border rounded-lg bg-secondary/20 w-full touch-pan-x touch-pan-y"
        style={{ 
          overflowX: "auto",
          overflowY: "visible",
          WebkitOverflowScrolling: "touch"
        }}
      >
        <div style={{ width: `${timelineWidth}px` }}>
          {/* Timeline Header */}
          <div className="flex border-b bg-muted/30 sticky top-0 z-10 items-stretch">
            {getTimelineBlocks().map((block, index) => (
              <div
                key={index}
                className={cn(
                  "flex flex-col justify-center gap-0.5 flex-shrink-0 text-center text-xs border-r border-border/50",
                  isExportMode ? "pt-4 pb-6 min-h-[80px] overflow-visible" : "pt-3 pb-4 min-h-[64px]",
                  block.isToday ? "bg-primary/10" : ""
                )}
                style={{ width: `${block.width}px` }}
              >
                <div
                  className={cn(
                    "font-medium px-1",
                    isExportMode
                      ? "overflow-visible whitespace-nowrap leading-normal pb-1"
                      : "leading-tight truncate",
                    block.isToday ? "text-primary" : ""
                  )}
                >
                  {block.label}
                </div>
                {timelineMode === "days" && (
                  <div
                    className={cn(
                      "text-[10px] px-1",
                      isExportMode ? "overflow-visible whitespace-nowrap leading-normal pb-1" : "leading-tight",
                      block.isToday ? "text-primary" : "text-muted-foreground"
                    )}
                  >
                    {block.date.toLocaleDateString("en-US", { weekday: "short" })}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Grid Lines */}
          <div 
            className="relative"
            style={{ 
              minHeight: items.length > 0 ? `${items.length * rowHeight + 20}px` : "80px",
              backgroundImage: `repeating-linear-gradient(90deg, transparent, transparent ${DAY_WIDTH - 1}px, hsl(var(--border) / 0.3) ${DAY_WIDTH - 1}px, hsl(var(--border) / 0.3) ${DAY_WIDTH}px)`,
            }}
          >
            {/* Today indicator line */}
            {(() => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const todayPos = dateToVisualPosition(today);
              if (todayPos >= 0 && todayPos <= timelineWidth) {
                return (
                  <div 
                    className="absolute top-0 bottom-0 w-0.5 bg-primary z-5"
                    style={{ left: `${todayPos}px` }}
                  />
                );
              }
              return null;
            })()}

            {items.length === 0 ? (
              <div className="flex items-center justify-center h-20 text-muted-foreground text-sm">
                No tasks yet. Click "Add Task" to create your first progress item.
              </div>
            ) : (
              items.map((item, index) => {
                const dateRange = getItemDateRange(item);
                const isItemDragging = isDragging && dragItemId === item.id;
                const visualPosition = getDisplayPosition(item);
                const visualWidth = getDisplayWidth(item);
                
                return (
                  <div
                    key={item.id}
                    data-gantt-bar="true"
                    className={`absolute rounded-md flex items-stretch group select-none ${
                      isItemDragging 
                        ? 'shadow-xl ring-2 ring-primary/50 z-30' 
                        : 'shadow-sm z-10 hover:shadow-md'
                    }`}
                    style={{
                      left: `${visualPosition}px`,
                      top: `${index * rowHeight + 10}px`,
                      width: `${visualWidth}px`,
                      height: `${barHeight}px`,
                      backgroundColor: item.color,
                      transition: isItemDragging ? 'none' : 'box-shadow 0.2s ease',
                      cursor: isItemDragging ? 'grabbing' : 'default',
                    }}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                  >
                    {/* External title label to the left — only when text overflows */}
                    {!isExportMode && overflowingItems.has(item.id) && (
                      <span
                        className="absolute right-full mr-2 top-1/2 -translate-y-1/2 text-xs font-semibold text-foreground whitespace-nowrap pointer-events-none"
                      >
                        {item.title}
                      </span>
                    )}

                    {/* Left resize handle — invisible until bar is hovered */}
                    {!isExportMode && (
                      <div
                        className="absolute left-0 top-0 bottom-0 w-3 cursor-ew-resize rounded-l-md flex items-center justify-center touch-none opacity-0 group-hover:opacity-100 transition-opacity bg-black/20"
                        onPointerDown={(e) => handlePointerDown(e, item, "resize-left")}
                      />
                    )}

                    {/* Content area - draggable */}
                    <div
                      className={cn(
                        "flex-1 flex items-center justify-start px-3 touch-none",
                        isExportMode ? "py-2 overflow-visible" : "overflow-hidden"
                      )}
                      style={{ cursor: isExportMode ? "default" : isItemDragging ? "grabbing" : "grab" }}
                      onPointerDown={isExportMode ? undefined : (e) => handlePointerDown(e, item, "move")}
                    >
                      <span
                        ref={(el) => {
                          if (el) titleRefs.current.set(item.id, el);
                          else titleRefs.current.delete(item.id);
                        }}
                        className={cn(
                          "text-xs font-semibold",
                          isExportMode ? "whitespace-normal break-words leading-tight" : "truncate",
                          !isExportMode && overflowingItems.has(item.id) ? "invisible" : ""
                        )}
                        style={{ color: "#ffffff" }}
                      >
                        {item.title}
                      </span>
                    </div>

                    {/* Right resize handle — invisible until bar is hovered */}
                    {!isExportMode && (
                      <div
                        className="absolute right-0 top-0 bottom-0 w-3 cursor-ew-resize rounded-r-md flex items-center justify-center touch-none opacity-0 group-hover:opacity-100 transition-opacity bg-black/20"
                        onPointerDown={(e) => handlePointerDown(e, item, "resize-right")}
                      />
                    )}

                    {/* Action buttons */}
                    {!isExportMode && (
                      <div className="absolute -right-16 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 z-30">
                        <Popover open={editingItem === item.id} onOpenChange={(open) => !open && setEditingItem(null)}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="secondary"
                              size="icon"
                              className="h-6 w-6"
                              onClick={() => startEditing(item)}
                            >
                              <Edit2 className="h-3 w-3" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-64 z-50 p-3" align="start" side="top" sideOffset={8}>
                            <div className="space-y-2">
                              <div>
                                <Label className="text-[10px] text-muted-foreground">Title</Label>
                                <Input
                                  value={editTitle}
                                  onChange={(e) => setEditTitle(e.target.value)}
                                  className="h-7 text-xs"
                                />
                              </div>
                              <div>
                                <Label className="text-[10px] text-muted-foreground">Notes</Label>
                                <Input
                                  value={editNotes}
                                  onChange={(e) => setEditNotes(e.target.value)}
                                  placeholder="Add description..."
                                  className="h-7 text-xs"
                                />
                              </div>
                              <div>
                                <Label className="text-[10px] text-muted-foreground">Color</Label>
                                <div className="flex gap-1 mt-1">
                                  {COLORS.map((color) => (
                                    <button
                                      key={color}
                                      className={`w-5 h-5 rounded-full border-2 ${
                                        item.color === color ? "border-foreground" : "border-transparent"
                                      }`}
                                      style={{ backgroundColor: color }}
                                      onClick={() => updateItem(item.id, { color })}
                                    />
                                  ))}
                                </div>
                              </div>
                              <div className="space-y-2">
                                <div className="flex gap-2">
                                  <div className="flex-1 min-w-0">
                                    <Label className="text-[10px] text-muted-foreground">Start Date</Label>
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <Button
                                          variant="outline"
                                          className={cn(
                                            "w-full h-7 justify-start text-left text-xs font-normal",
                                            !editStartDate && "text-muted-foreground"
                                          )}
                                        >
                                          <CalendarIcon className="mr-1 h-3 w-3 shrink-0" />
                                          <span className="truncate">
                                            {editStartDate ? format(new Date(editStartDate + 'T00:00:00'), "MMM d, yyyy") : "Pick date"}
                                          </span>
                                        </Button>
                                      </PopoverTrigger>
                                      <PopoverContent className="w-auto p-0 z-[100]" align="start" side="top">
                                        <CalendarComponent
                                          mode="single"
                                          selected={editStartDate ? new Date(editStartDate + 'T00:00:00') : undefined}
                                          onSelect={(date) => {
                                            if (!date) return;
                                            const y = date.getFullYear();
                                            const m = String(date.getMonth() + 1).padStart(2, '0');
                                            const d = String(date.getDate()).padStart(2, '0');
                                            setEditStartDate(`${y}-${m}-${d}`);
                                          }}
                                          initialFocus
                                          className={cn("p-1 pointer-events-auto text-xs")}
                                          classNames={{
                                            months: "flex flex-col",
                                            month: "space-y-1",
                                            caption: "flex justify-center pt-0.5 relative items-center",
                                            caption_label: "text-xs font-medium",
                                            nav_button: "h-5 w-5 bg-transparent p-0 opacity-50 hover:opacity-100",
                                            table: "w-full border-collapse",
                                            head_row: "flex",
                                            head_cell: "text-muted-foreground rounded-md w-6 font-normal text-[10px]",
                                            row: "flex w-full mt-0.5",
                                            cell: "h-6 w-6 text-center text-[10px] p-0 relative",
                                            day: "h-6 w-6 p-0 font-normal text-[10px] aria-selected:opacity-100 hover:bg-accent rounded-md",
                                            day_selected: "bg-primary text-primary-foreground hover:bg-primary",
                                            day_today: "bg-accent text-accent-foreground",
                                            day_outside: "text-muted-foreground opacity-50",
                                          }}
                                        />
                                      </PopoverContent>
                                    </Popover>
                                  </div>
                                  <div className="w-[68px] shrink-0">
                                    <Label className="text-[10px] text-muted-foreground">Days</Label>
                                    <Input
                                      type="number"
                                      min={1}
                                      max={365}
                                      value={editDuration}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setEditDuration(val === '' ? '' : (parseInt(val) || ''));
                                      }}
                                      onBlur={() => {
                                        if (editDuration === '' || Number(editDuration) < 1) {
                                          setEditDuration(1);
                                        }
                                      }}
                                      className="h-7 text-xs"
                                    />
                                  </div>
                                </div>
                                <div className={cn(
                                  "flex gap-2 transition-opacity",
                                  editIsAllDay ? "opacity-40 pointer-events-none" : ""
                                )}>
                                  <div className="flex-1 min-w-0">
                                    <Label className="text-[10px] text-muted-foreground">Time of day</Label>
                                    <div className="relative">
                                      <Clock className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground pointer-events-none" />
                                      <Input
                                        type="time"
                                        value={editStartTime}
                                        onChange={(e) => setEditStartTime(e.target.value)}
                                        className="h-7 text-xs w-full pl-7 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:left-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
                                        disabled={editIsAllDay}
                                      />
                                    </div>
                                  </div>
                                  <div className="w-[80px] shrink-0">
                                    <Label className="text-[10px] text-muted-foreground">Duration</Label>
                                    <div className="relative">
                                      <Input
                                        type="number"
                                        min={15}
                                        max={480}
                                        step={15}
                                        value={editTimeDuration}
                                        onChange={(e) => setEditTimeDuration(parseInt(e.target.value) || 60)}
                                        className="h-7 text-xs pr-7"
                                        disabled={editIsAllDay}
                                      />
                                      <span className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] text-muted-foreground pointer-events-none">min</span>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <Checkbox
                                    id={`allday-${item.id}`}
                                    checked={editIsAllDay}
                                    onCheckedChange={(checked) => setEditIsAllDay(!!checked)}
                                    className="h-3.5 w-3.5"
                                  />
                                  <Label htmlFor={`allday-${item.id}`} className="text-[10px] text-muted-foreground cursor-pointer">
                                    All day event
                                  </Label>
                                </div>
                              </div>
                              <div className="flex justify-end gap-1 pt-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 px-2 text-xs"
                                  onClick={() => setEditingItem(null)}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  className="h-6 px-2 text-xs"
                                  onClick={() => saveEdit(item.id)}
                                >
                                  Save
                                </Button>
                              </div>
                            </div>
                          </PopoverContent>
                        </Popover>
                        <Button
                          variant="destructive"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => deleteItem(item.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    )}

                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {items.length > 0 && (
        <p className="text-xs text-muted-foreground">
          <ChevronLeft className="h-3 w-3 inline" /><ChevronRight className="h-3 w-3 inline" /> Drag edges to resize • Drag bar to move • Hover for edit/delete
        </p>
      )}
    </div>
  );
};

export default LeadGanttChart;
